/**
 * View Component: InstallButton
 * T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #11「PWA:安装到桌面」。
 *
 * 只在**浏览器真的支持**时显示。理由:
 *   国产 WebView(华为/小米/OPPO 定制内核)有两种表现 ——
 *   有的完全不派发 `beforeinstallprompt`,有的派发了但 prompt() 无效。
 *   给用户一个「点了没反应」的按钮比不给更糟 ⇒ 不支持就**不渲染**。
 *
 * 已经在 standalone 里跑(用户已经装过了)同样不渲染。
 *
 * 交互细节:
 *   · 点了之后立刻 disable,避免连点多次触发 prompt() 抛错。
 *   · 用户拒绝(dismissed)后本次会话不再显示 —— 反复骚扰没意义。
 *     但刷新页面后会重新评估(事件只派发一次,通常也就没了)。
 *
 * 本文件是 .jsx 且含 JSX ⇒ React 由 vite.config.js 的 esbuild.jsxInject
 * 全局注入,这里**不要**显式 import React。
 */
import { canInstall, isStandalone, promptInstall, subscribeInstallState } from '../harness/installPrompt.js';
import { buzz } from '../harness/haptics.js';

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
  );
}

function InstallButton() {
  const [available, setAvailable] = React.useState(() => canInstall());
  const [installed, setInstalled] = React.useState(() => isStandalone());
  const [busy, setBusy] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    // 订阅安装状态:beforeinstallprompt 派发时(可能在我们挂载之后)
    // 会 notify ⇒ 按钮会自己冒出来。
    const unsubscribe = subscribeInstallState((state) => {
      setAvailable(!!state.canInstall);
      setInstalled(!!state.isInstalled);
    });
    return unsubscribe;
  }, []);

  if (installed) return null;
  if (!available || dismissed) return null;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    buzz(12);
    const outcome = await promptInstall();
    setBusy(false);
    if (outcome === 'accepted') {
      // 装成功了 ⇒ 按钮没必要再出现。
      setInstalled(true);
    } else if (outcome === 'dismissed') {
      // 用户明确拒绝 ⇒ 本次会话不再打扰。
      setDismissed(true);
    } else {
      // unavailable:prompt() 无效(国产 WebView 常见)⇒ 隐藏掉这个假按钮。
      setAvailable(false);
    }
  };

  return (
    <button
      className="header-install-btn"
      onClick={handleClick}
      disabled={busy}
      aria-label="安装到桌面，像 App 一样使用"
      title="安装到桌面（支持离线打开）"
    >
      <DownloadIcon />
      <span className="header-install-label">{busy ? '安装中' : '安装'}</span>
    </button>
  );
}

window.InstallButton = InstallButton;

export default InstallButton;
